// ─────────────────────────────────────────────────────────────────────────────
// Trending, typed client for the daily spike feed. (trending 2026-07-06)
//
// GET /api/trending (auth worker) returns the band's live Trending items:
// ≥15% day-over-day spikes across per-post views (IG/TikTok/FB/YT), platform
// stats (followers/subscribers/monthly listeners/streams) and named Spotify
// tracks, detected by the worker's daily cron sweep (runTrendingSweep),
// 7-day TTL, pruned server-side on read. FAIL-EMPTY: any error → [].
//
// Scoping (Bible 06.4): the artist surfaces call fetchTrendingItems(), the
// standard authHeaders carry X-Band-Id when impersonation is active. The
// roster surfaces call fetchTrendingItems(artistId) with the EXACT selected
// artist id so the worker's reach gate (resolveTargetBandId middleware)
// scopes the read; unreachable artists come back empty, never someone else's.
//
// This module also owns the shared COPY builders so the desktop card, the
// roster card, and the mobile card all say exactly the same thing (Ryan's
// wording): pills like «"<Song>" had a major jump in streams on Spotify» /
// «This Instagram post saw a big spike», and the detail recommendation copy
// with the external platform link. Percentages render rounded ("+23%");
// platform names proper-cased; dates local-device (never UTC-anchored).
// ─────────────────────────────────────────────────────────────────────────────

import { AUTH_WORKER } from './api';
import { authHeaders, getToken } from './auth';

/** One daily value behind a spike, 'YYYY-MM-DD' + the value the sweep's
    detection math saw for that day. (trend-viz 2026-07-06) */
export interface TrendingSeriesPoint {
  readonly d: string;
  readonly v: number;
}

/** One Trending item as stored/served by the worker. */
export interface TrendingItem {
  readonly id: string;
  readonly kind: 'post' | 'track' | 'platform';
  /** Lowercase platform id: instagram | tiktok | facebook | youtube | spotify | threads. */
  readonly platform: string;
  /** Dedupe subject key (kind:platform:id), also the stable React key. */
  readonly subject?: string;
  /** Post caption / track name ('' for platform-level items). */
  readonly title?: string;
  /** Thumbnail (song artwork / post image / video cover proxy URL). */
  readonly thumb?: string;
  /** External link (post permalink / share_url / artists.spotify.com). */
  readonly url?: string;
  /** Metric that spiked: views | streams | followers | subscribers | monthly listeners. */
  readonly metric: string;
  /** Rounded day-over-day rise, e.g. 23 → "+23%". */
  readonly pct: number;
  readonly gain?: number;
  /** Detection day, 'YYYY-MM-DD'. Items expire 7 days after this. */
  readonly detected: string;
  /** Up to the last 14 daily values behind the spike (ascending, spike day
      LAST), captured at detection time from the exact series the sweep's
      math ran on. Absent on items detected before capture shipped: the
      detail views just skip the sparkline. (trend-viz 2026-07-06) */
  readonly series?: ReadonlyArray<TrendingSeriesPoint>;
}

interface TrendingResponse {
  readonly success?: boolean;
  readonly items?: ReadonlyArray<TrendingItem>;
}

/**
 * Fetch the band's Trending items. Pass `artistId` ONLY from roster surfaces
 * (explicit X-Band-Id == the selected artist, the Bible 06.4 contract);
 * artist/mobile surfaces omit it and ride the standard header set.
 * Fail-empty: network/auth errors return [].
 */
export async function fetchTrendingItems(artistId?: string): Promise<ReadonlyArray<TrendingItem>> {
  try {
    const headers: Record<string, string> = artistId
      ? { Authorization: 'Bearer ' + getToken(), 'X-Band-Id': artistId }
      : authHeaders('GET');
    const res = await fetch(AUTH_WORKER + '/api/trending', { headers });
    if (!res.ok) return [];
    const body = (await res.json()) as TrendingResponse;
    return Array.isArray(body.items) ? body.items.filter((it) => !!(it && it.id)) : [];
  } catch {
    return [];
  }
}

/** Proper-cased platform name ("tiktok" → "TikTok"). */
export function trendingPlatformLabel(platform: string): string {
  const MAP: Record<string, string> = {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    facebook: 'Facebook',
    youtube: 'YouTube',
    spotify: 'Spotify',
    threads: 'Threads',
  };
  const key = String(platform || '').toLowerCase();
  return MAP[key] || (key ? key.charAt(0).toUpperCase() + key.slice(1) : '');
}

/** "+23%", rounded, always signed up (only positive spikes are stored). */
export function trendingPctLabel(item: Pick<TrendingItem, 'pct'>): string {
  return '+' + Math.round(Number(item.pct) || 0) + '%';
}

/** "video" for TikTok/YouTube, "post" for the feed platforms. */
function postNoun(platform: string): string {
  const p = String(platform || '').toLowerCase();
  return p === 'tiktok' || p === 'youtube' ? 'video' : 'post';
}

function truncate(s: string, max: number): string {
  const t = String(s || '').trim();
  return t.length > max ? t.slice(0, max - 1).trimEnd() + '…' : t;
}

/**
 * Detection day for display, LOCAL device date, 12-hour-rule compliant
 * ('T12:00:00' local-noon anchor, never a trailing Z). "Today"/"Yesterday"
 * for the fresh cases, then "Jul 5".
 */
export function trendingWhenLabel(item: Pick<TrendingItem, 'detected'>): string {
  const d = new Date(String(item.detected || '') + 'T12:00:00');
  if (isNaN(d.getTime())) return '';
  const today = new Date();
  const midnight = (x: Date): number => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((midnight(today) - midnight(d)) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Pill headline, Ryan's wording per kind. */
export function trendingHeadline(item: TrendingItem): string {
  const platform = trendingPlatformLabel(item.platform);
  if (item.kind === 'track') {
    return '“' + truncate(item.title || 'Your track', 48) + '” had a major jump in streams on ' + platform;
  }
  if (item.kind === 'post') {
    return 'This ' + platform + ' ' + postNoun(item.platform) + ' saw a big spike in views';
  }
  // platform-level metric ("Instagram followers jumped +23%")
  return platform + ' ' + item.metric + ' jumped ' + trendingPctLabel(item);
}

/** Detail-view recommendation copy + external link for one item. */
export interface TrendingDetail {
  readonly heading: string;
  readonly body: string;
  /** External link (opens new tab on desktop / external browser on mobile). */
  readonly linkUrl: string;
  readonly linkLabel: string;
  /** Whether to nudge toward the app's Content tab for ideas. */
  readonly contentHint: boolean;
}

export function trendingDetail(item: TrendingItem): TrendingDetail {
  const platform = trendingPlatformLabel(item.platform);
  const pct = trendingPctLabel(item);

  if (item.kind === 'track') {
    return {
      heading: '“' + truncate(item.title || 'Your track', 60) + '” is trending',
      body:
        '“' + (item.title || 'Your track') + '” had a major spike in streaming (' + pct +
        ' in a day)! We recommend you keep a close eye on it and create some content using this song. ' +
        'To see where the streams came from, visit Spotify for Artists.',
      linkUrl: item.url || 'https://artists.spotify.com',
      linkLabel: 'Spotify for Artists',
      contentHint: false,
    };
  }

  if (item.kind === 'post') {
    const noun = postNoun(item.platform);
    const captioned = item.title ? ' “' + truncate(item.title, 60) + '”' : '';
    return {
      heading: 'Your ' + platform + ' ' + noun + ' is trending',
      body:
        'Your ' + platform + ' ' + noun + captioned + ' had a big spike in views (' + pct +
        ' in a day)! Keep a close eye on it and create similar content using the same audio & style. ' +
        'Use the Content tab to help with ideas.',
      linkUrl: item.url || '',
      linkLabel: item.url ? 'Open on ' + platform : '',
      contentHint: true,
    };
  }

  // platform-level
  const isSpotify = String(item.platform || '').toLowerCase() === 'spotify';
  const streamsy = /stream|listener/.test(String(item.metric || ''));
  const body = isSpotify
    ? 'Your Spotify ' + item.metric + ' had a major spike (' + pct + ' in a day)! We recommend you ' +
      'keep a close eye on it' + (streamsy ? ' and create some content while the momentum is hot' : '') +
      '. To see where the ' + (streamsy ? 'streams' : 'growth') + ' came from, visit Spotify for Artists.'
    : 'Your ' + platform + ' ' + item.metric + ' had a big spike (' + pct + ' in a day)! Momentum like ' +
      'this is worth riding, post while the algorithm is favoring you. Use the Content tab to help with ideas.';
  return {
    heading: platform + ' ' + item.metric + ' are trending',
    body,
    linkUrl: isSpotify ? (item.url || 'https://artists.spotify.com') : (item.url || ''),
    linkLabel: isSpotify ? 'Spotify for Artists' : (item.url ? 'Open ' + platform : ''),
    contentHint: !isSpotify,
  };
}
